/**
 * Tour-wide money panel for the Expenses page: profit and loss, budget vs actuals, and
 * settlement reminders. Also exports the reminders strip used at the top of Settlement.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Platform, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { formatUsd, numericText, parseWorksheetSeed, NUM } from '../../utils/calendarSettlement';
import {
  buildTourPnl,
  parseBudget,
  serializeBudget,
  tourPnlToCsv,
  tourPnlToPrintHtml,
  openPrintWindow,
  settlementReminders,
} from '../../utils/calendarTourPnl';

const mono = { fontVariant: ['tabular-nums'] };

/** Parse the raw tab-entry texts the page loads into the shapes the util wants. */
export function parseFinance(finance) {
  const settlements = {};
  Object.entries(finance?.settlements || {}).forEach(([k, text]) => {
    const sheet = parseWorksheetSeed(text, null);
    if (sheet) settlements[k] = sheet;
  });
  const ledgers = {};
  Object.entries(finance?.ledgers || {}).forEach(([k, text]) => {
    const ledger = parseWorksheetSeed(text, null);
    if (ledger) ledgers[k] = ledger;
  });
  return { settlements, ledgers, legLedger: parseWorksheetSeed(finance?.leg, null) };
}

export function RemindersStrip({ pal, finance, todayKey, onSelectDate }) {
  const reminders = useMemo(
    () => settlementReminders({ settlements: parseFinance(finance).settlements, todayKey }),
    [finance, todayKey]
  );
  if (!reminders.length) return null;
  return (
    <View style={{ marginTop: 14, padding: 12, borderWidth: 1, borderColor: pal.line, borderRadius: 10, backgroundColor: pal.sunk, gap: 6 }}>
      <Text style={{ color: pal.mute, fontSize: 11, fontWeight: '700', letterSpacing: 0.8, textTransform: 'uppercase' }}>
        Needs attention · {reminders.length}
      </Text>
      {reminders.slice(0, 8).map((r) => (
        <TouchableOpacity key={r.id} disabled={!onSelectDate} onPress={() => onSelectDate?.(r.dateKey)} accessibilityLabel={r.text}>
          <Text style={{ color: r.level === 'alert' ? pal.alert : pal.ink, fontSize: 14 }}>
            {r.level === 'alert' ? '● ' : '○ '}{r.text}
          </Text>
        </TouchableOpacity>
      ))}
      {reminders.length > 8 ? <Text style={{ color: pal.mute, fontSize: 13 }}>…and {reminders.length - 8} more</Text> : null}
    </View>
  );
}

function Stat({ pal, k, v, color }) {
  return (
    <View style={{ minWidth: 120 }}>
      <Text style={{ color: pal.mute, fontSize: 11, fontWeight: '700', letterSpacing: 0.8, textTransform: 'uppercase' }}>{k}</Text>
      <Text style={[{ color: color || pal.ink, fontSize: 20, fontWeight: '700', marginTop: 2 }, mono]}>{v}</Text>
    </View>
  );
}

function Row({ pal, cells, bold, color }) {
  return (
    <View style={{ flexDirection: 'row', gap: 10, paddingVertical: 6, borderTopWidth: 1, borderTopColor: pal.lineSoft }}>
      {cells.map((c, i) => (
        <Text
          key={i}
          style={[
            { color: color || pal.ink, fontSize: 14, fontWeight: bold ? '700' : '400', flex: i === 0 ? 2 : 1, textAlign: i === 0 ? 'left' : 'right' },
            i === 0 ? null : mono,
          ]}
        >
          {c}
        </Text>
      ))}
    </View>
  );
}

export default function TourFinancePanel({ pal, finance, tourName = '', canEdit = true, onSaveBudget, accent }) {
  const parsed = useMemo(() => parseFinance(finance), [finance]);
  const incomingBudget = finance?.budget || '';
  const [budget, setBudget] = useState(() => parseBudget(incomingBudget));
  // Snapshots echo our own saves back, sometimes after newer keystrokes; only adopt text we did not send.
  const sentValues = useRef(new Set([incomingBudget]));
  const lastSent = useRef(incomingBudget);
  useEffect(() => {
    if (sentValues.current.has(incomingBudget)) return;
    lastSent.current = incomingBudget;
    sentValues.current.add(incomingBudget);
    setBudget(parseBudget(incomingBudget));
  }, [incomingBudget]);
  const pnl = useMemo(() => buildTourPnl({ ...parsed, budget }), [parsed, budget]);
  const t = pnl.totals;

  const saveBudget = (next) => {
    if (!canEdit) return;
    setBudget(next);
    const json = serializeBudget(next);
    lastSent.current = json;
    sentValues.current.add(json);
    onSaveBudget?.(json);
  };
  const editLine = (i, patch) => saveBudget({ lines: budget.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) });
  const addLine = (label = '', amount = 0) => saveBudget({ lines: [...budget.lines, { label, amount }] });
  const seedFromActuals = () => {
    const have = new Set(budget.lines.map((l) => String(l.label).trim().toLowerCase()));
    const extra = pnl.expenseCategories
      .filter((c) => !have.has(String(c.label).trim().toLowerCase()))
      .map((c) => ({ label: c.label, amount: 0 }));
    if (extra.length) saveBudget({ lines: [...budget.lines, ...extra] });
  };

  const web = Platform.OS === 'web' && typeof document !== 'undefined';
  const downloadCsv = () => {
    if (!web || typeof Blob === 'undefined') return;
    const csv = tourPnlToCsv(pnl, { tourName });
    const url = URL.createObjectURL(new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `tour-pnl-${String(tourName || 'tour').replace(/[^\w-]+/g, '_')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };
  const [printNote, setPrintNote] = useState('');
  const printPdf = () => {
    const ok = openPrintWindow(tourPnlToPrintHtml(pnl, { tourName }));
    setPrintNote(ok ? '' : 'Allow pop-ups to print or save as PDF.');
  };

  const btn = { paddingVertical: 6, paddingHorizontal: 12, borderWidth: 1, borderColor: pal.line, borderRadius: 8 };
  const empty = !pnl.shows.length && !pnl.expenseCategories.length && !t.otherIncome;

  return (
    <View style={{ marginTop: 28, borderTopWidth: 2, borderTopColor: accent || pal.teal, paddingTop: 16 }}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
        <View>
          <Text style={{ color: pal.mute, fontSize: 11, fontWeight: '700', letterSpacing: 0.8, textTransform: 'uppercase' }}>Whole tour</Text>
          <Text style={{ color: pal.ink, fontSize: 24, fontWeight: '700', marginTop: 2 }}>Profit &amp; loss and budget</Text>
        </View>
        {web ? (
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <TouchableOpacity onPress={downloadCsv} accessibilityLabel="Download tour P&L CSV" style={btn}>
              <Text style={{ color: pal.mute, fontWeight: '600', fontSize: 12 }}>Download CSV</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={printPdf} accessibilityLabel="Print or save tour P&L as PDF" style={btn}>
              <Text style={{ color: pal.mute, fontWeight: '600', fontSize: 12 }}>Print / PDF</Text>
            </TouchableOpacity>
          </View>
        ) : null}
      </View>
      {printNote ? <Text style={{ color: pal.warn, marginTop: 8, fontSize: 13 }}>{printNote}</Text> : null}
      <Text style={{ color: pal.mute, fontSize: 13.5, marginTop: 6 }}>
        Adds up every settled show and every day ledger on the tour. Artist earning comes from each Settlement sheet; costs come from the day and leg ledgers.
      </Text>

      {empty ? (
        <Text style={{ color: pal.mute, marginTop: 14 }}>Nothing to add up yet. Fill in a Settlement sheet or add ledger lines and the totals appear here.</Text>
      ) : (
        <>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 22, marginTop: 14 }}>
            <Stat pal={pal} k="Income" v={formatUsd(t.totalIncome)} color={pal.ok} />
            <Stat pal={pal} k="Expenses" v={formatUsd(t.totalExpenses)} color={pal.alert} />
            <Stat pal={pal} k="Net" v={formatUsd(t.net)} color={t.net < 0 ? pal.alert : pal.ok} />
            <Stat pal={pal} k="Margin" v={t.marginPct == null ? '—' : `${t.marginPct}%`} />
            <Stat pal={pal} k="Received" v={formatUsd(t.received)} />
            <Stat pal={pal} k="Still owed" v={formatUsd(t.outstanding)} color={t.outstanding > 0 ? pal.warn : pal.ink} />
          </View>

          {pnl.shows.length ? (
            <View style={{ marginTop: 18 }}>
              <Text style={{ color: pal.mute, fontSize: 11, fontWeight: '700', letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>Shows</Text>
              <Row pal={pal} bold cells={['Date', 'Gross', 'Earned', 'Paid', 'Balance']} color={pal.mute} />
              {pnl.shows.map((s) => (
                <Row
                  key={s.dateKey}
                  pal={pal}
                  cells={[s.dateKey, formatUsd(s.gross), formatUsd(s.earned), formatUsd(s.paidToDate), formatUsd(s.balance)]}
                />
              ))}
            </View>
          ) : null}
        </>
      )}

      <View style={{ marginTop: 22 }}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <Text style={{ color: pal.mute, fontSize: 11, fontWeight: '700', letterSpacing: 0.8, textTransform: 'uppercase' }}>
            Budget vs actual{t.budget > 0 ? ` · ${formatUsd(t.budget)} planned` : ''}
          </Text>
          {canEdit ? (
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <TouchableOpacity onPress={() => addLine()} accessibilityLabel="Add budget line" style={btn}>
                <Text style={{ color: pal.mute, fontWeight: '600', fontSize: 12 }}>Add budget line</Text>
              </TouchableOpacity>
              {pnl.expenseCategories.length ? (
                <TouchableOpacity onPress={seedFromActuals} accessibilityLabel="Add lines from actual spending" style={btn}>
                  <Text style={{ color: pal.mute, fontWeight: '600', fontSize: 12 }}>Add lines from spending</Text>
                </TouchableOpacity>
              ) : null}
            </View>
          ) : null}
        </View>
        {t.overBudget ? (
          <Text style={{ color: pal.alert, marginTop: 8, fontWeight: '600' }}>
            Over budget by {formatUsd(t.totalExpenses - t.budget)}.
          </Text>
        ) : null}
        {budget.lines.length ? (
          <View style={{ marginTop: 8 }}>
            <Row pal={pal} bold cells={['Line', 'Budget', 'Actual', 'Left']} color={pal.mute} />
            {budget.lines.map((line, i) => {
              const row = pnl.budgetRows[i];
              const over = row?.over;
              return (
                <View key={i} style={{ flexDirection: 'row', gap: 10, alignItems: 'center', paddingVertical: 4, borderTopWidth: 1, borderTopColor: pal.lineSoft }}>
                  <TextInput
                    editable={canEdit}
                    value={line.label}
                    onChangeText={(v) => editLine(i, { label: v })}
                    placeholder="e.g. Fuel"
                    placeholderTextColor={pal.faint}
                    style={{ flex: 2, color: pal.ink, fontSize: 14, paddingVertical: 4 }}
                  />
                  <TextInput
                    editable={canEdit}
                    value={line.amount === 0 || line.amount == null ? '' : String(line.amount)}
                    onChangeText={(v) => editLine(i, { amount: numericText(v) })}
                    inputMode="decimal"
                    keyboardType="numeric"
                    placeholder="0"
                    placeholderTextColor={pal.faint}
                    style={[{ flex: 1, color: pal.ink, fontSize: 14, textAlign: 'right', paddingVertical: 4 }, mono]}
                  />
                  <Text style={[{ flex: 1, color: pal.ink, fontSize: 14, textAlign: 'right' }, mono]}>{formatUsd(row?.actual || 0)}</Text>
                  <Text style={[{ flex: 1, color: over ? pal.alert : pal.ok, fontSize: 14, fontWeight: '600', textAlign: 'right' }, mono]}>
                    {formatUsd(row?.variance || 0)}
                  </Text>
                  {canEdit ? (
                    <TouchableOpacity onPress={() => saveBudget({ lines: budget.lines.filter((_, j) => j !== i) })} accessibilityLabel="Remove budget line">
                      <Text style={{ color: pal.mute, fontSize: 16, width: 22, textAlign: 'center' }}>×</Text>
                    </TouchableOpacity>
                  ) : null}
                </View>
              );
            })}
            {pnl.budgetRows.filter((r) => r.unbudgeted).map((r) => (
              <Row key="unbudgeted" pal={pal} cells={[r.label, '', formatUsd(r.actual), formatUsd(r.variance)]} color={pal.warn} />
            ))}
          </View>
        ) : (
          <Text style={{ color: pal.mute, marginTop: 8, fontSize: 13.5 }}>
            Plan what each cost should be (fuel, hotels, crew) and the page shows how much is left as the ledgers fill in. Names match the ledger line labels.
          </Text>
        )}
      </View>
    </View>
  );
}
